import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Box, Divider, Stack, Typography, useTheme } from "@mui/material";
import { useReportContext } from "../../../components/Report/ReportContext/useReportContext";
import { IndicatorsList } from "../../../components/MainComponents/Indicators/IndicatorsList";
import { EGIconButton } from "../../../components/MainComponents/Button/IconButton/IconButton";
import { ReportGeneralInfo } from "../../../components/Report/Lists/ReportGeneralInfo";
import useLocalizedLabel from "../../../hooks/useLocalizedLabel";
import useTagsCatalog from "../../../hooks/useTagsCatalog";
import Icon from "../../../components/Icon/Icon";
import { HighlightText } from "../HighlightText";
import {
  useInvolvedColors,
  useInvolvedDetail,
  useInvolvedTypeLabel,
} from "../../../hooks/useInvolvedDisplay";
import {
  formatResultDate,
  MetaText,
  resultActionsSx,
  resultDividerSx,
  resultRowSx,
  resultTitleSx,
  StatusTag,
} from "./resultBits";

/** Reporte del endpoint -> datos que pinta la lista */
const transformReport = (report, statusLocales) => {
  const draftEntry = report?.status?.history?.find(
    (entry) => entry.status === "draft"
  );

  return {
    id: report.id,
    folio: report.folio,
    tracking_code: report.tracking_code,
    status: statusLocales[report.status.current]?.label,
    statusColor: statusLocales[report.status.current]?.color,
    subject: report.subject,
    // El endpoint todavía no devuelve un extracto del texto que hizo match;
    // cuando lo haga se pinta aquí sin tocar el resto de la lista.
    snippet: report.snippet || "",
    // Quién empató al buscar por involucrado. Solo viene cuando se buscó por
    // ese campo; en cualquier otra búsqueda la lista llega vacía.
    involvedMatches: Array.isArray(report.involved_matches)
      ? report.involved_matches
      : [],
    created_at: formatResultDate(
      draftEntry?.updated_at || report?.status?.assigned
    ),
    entryway: report.entryway,
    classification: report.classification,
    business_unit: report.business_unit,
    indicators: report.custom_client_info?.indicators,
    tags: Array.isArray(report?.custom_client_info?.tags)
      ? report.custom_client_info.tags
      : [],
  };
};

export const ReportResults = ({ results = [], query = "" }) => {
  const [t] = useTranslation("report");
  const theme = useTheme();
  const navigate = useNavigate();
  const { status_locales, entryways, icon_indicators } = useReportContext();
  const getLocalizedLabel = useLocalizedLabel();
  const transformedType = useTagsCatalog();
  const involvedColors = useInvolvedColors();
  const involvedDetail = useInvolvedDetail();
  const involvedTypeLabel = useInvolvedTypeLabel();

  const [showReportSummary, setShowReportSummary] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const toggleDrawer =
    (open, row = null) =>
    () => {
      setShowReportSummary(open);
      setSelectedRow(row);
    };

  const rows = results.map((report) => transformReport(report, status_locales));

  return (
    <>
      <ReportGeneralInfo
        showReportSummary={showReportSummary}
        setShowReportSummary={setShowReportSummary}
        setSelectedRow={setSelectedRow}
        selectedRow={selectedRow}
        toggleDrawer={toggleDrawer}
        transformedType={transformedType}
        getLocalizedLabel={getLocalizedLabel}
        icon_indicators={icon_indicators}
        translation={t}
      />

      <Stack divider={<Divider sx={resultDividerSx(theme)} />}>
        {rows.map((row) => {
          const breadcrumb = [
            entryways[row.entryway]?.label,
            row.classification,
            row.business_unit,
          ].filter(Boolean);

          return (
            <Stack key={row.id} sx={resultRowSx(theme)}>
              {/* Línea de contexto: estado · folio · seguimiento · fecha */}
              <Stack
                sx={{
                  flexFlow: "row wrap",
                  alignItems: "center",
                  columnGap: 1,
                  rowGap: 0.25,
                  mb: 0.5,
                }}
              >
                <StatusTag label={row.status} color={row.statusColor} />
                <Typography variant="body2" fontSize={12} fontWeight={600}>
                  <HighlightText text={row.folio || ""} query={query} />
                </Typography>
                <MetaText
                  items={[
                    row.tracking_code && (
                      <>
                        {t("searchBar.filter_tracking_code")}:{" "}
                        <HighlightText
                          text={row.tracking_code}
                          query={query}
                        />
                      </>
                    ),
                    row.created_at,
                  ]}
                />
              </Stack>

              {/* Título: el asunto lleva al reporte */}
              <Typography
                color="primary"
                sx={{
                  ...resultTitleSx,
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
                onClick={() => navigate(`/reports/${row.id}`)}
              >
                <HighlightText
                  text={row.subject || t("ReportsList.no_subject")}
                  query={query}
                />
              </Typography>

              {row.snippet && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5, maxWidth: "min(100%, 90ch)" }}
                >
                  <HighlightText text={row.snippet} query={query} />
                </Typography>
              )}

              {/* Quién empató: el involucrado y su rol, que es lo que explica
                  el resultado cuando se busca por involucrados. */}
              {row.involvedMatches.map((person, index) => (
                <Stack
                  key={`${person.type}-${person.name}-${index}`}
                  direction="row"
                  spacing={0.75}
                  sx={{ alignItems: "flex-start", mt: 0.5 }}
                >
                  <Icon
                    name="outline_profile_2_user"
                    size={14}
                    color={
                      involvedColors[person.type] ||
                      theme.palette.blueGray500.main
                    }
                    style={{ marginTop: 3, flexShrink: 0 }}
                  />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ maxWidth: "min(100%, 90ch)" }}
                  >
                    <Box
                      component="span"
                      sx={{
                        mr: 0.5,
                        fontWeight: 600,
                        color:
                          involvedColors[person.type] || "text.primary",
                      }}
                    >
                      {involvedTypeLabel(person.type)}:
                    </Box>
                    <HighlightText
                      text={involvedDetail(person)}
                      query={query}
                    />
                  </Typography>
                </Stack>
              ))}

              {breadcrumb.length > 0 && (
                <MetaText items={breadcrumb} sx={{ mt: 0.75 }} />
              )}

              <Stack
                sx={{
                  mt: 1,
                  flexFlow: "row wrap",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  minHeight: 32,
                }}
              >
                <IndicatorsList
                  indicators={row.indicators}
                  entryway={row.entryway}
                  classification={row.classification}
                  tags={row.tags}
                  transformedType={transformedType}
                  getLocalizedLabel={getLocalizedLabel}
                  icon_indicators={icon_indicators}
                  t={t}
                  theme={theme}
                  iconSize={16}
                />
                <Box className="search-result-actions" sx={resultActionsSx}>
                  <Stack direction="row" spacing={0.5}>
                    <EGIconButton
                      title={t("ReportsList.summary")}
                      iconName="outline_maximize_3"
                      iconColorHover="secondary"
                      iconSize={18}
                      onClick={toggleDrawer(true, row)}
                    />
                    <EGIconButton
                      title={t("ReportsList.view_report")}
                      iconName="outline_eye"
                      iconColor="primary"
                      iconSize={18}
                      onClick={() => navigate(`/reports/${row.id}`)}
                    />
                  </Stack>
                </Box>
              </Stack>
            </Stack>
          );
        })}
      </Stack>
    </>
  );
};
